import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import { initialsOf } from '../chatUtils';


/**
 * Панель состава группы: список участников, добавление/удаление для
 * владельца и админа, выход из группы для всех, кроме владельца.
 */
const ParticipantsPanel = ({
  chat, currentUserId, canManage, myRole, onClose, onAddMembers, onRemoveMember, onLeave,
}) => {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [error, setError] = useState(null);

  const participants = chat?.participants || [];

  const handleRemove = async (participant) => {
    setBusyId(participant.userId);
    setError(null);
    try {
      await onRemoveMember(participant.userId);
    } catch (e) {
      setError(e?.message || t('chat.participants.removeError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleLeave = async () => {
    setBusyId('self');
    setError(null);
    try {
      await onLeave();
    } catch (e) {
      setError(e?.message || t('chat.participants.leaveError'));
      setBusyId(null);
    }
  };

  return (
    <aside className="chat-participants">
      <header className="chat-participants-head">
        <div>
          <h4 className="chat-participants-title">{t('chat.participants.title')}</h4>
          <p className="chat-participants-count">{t('chat.participants.count', { count: participants.length })}</p>
        </div>
        <button type="button" className="chat-icon-btn" onClick={onClose} title={t('calendar.dialog.close')}>
          <Icon name="x" size={18} />
        </button>
      </header>

      {canManage && (
        <button type="button" className="chat-participants-add" onClick={onAddMembers}>
          <Icon name="user-plus" size={16} />
          {t('chat.participants.add')}
        </button>
      )}

      {error && <div className="chat-composer-error">{error}</div>}

      <ul className="chat-participants-list">
        {participants.map((participant) => {
          const isMe = String(participant.userId) === String(currentUserId);
          const isOwner = participant.role === 'OWNER';
          // Владельца и себя удалить нельзя — из группы выходят кнопкой ниже.
          const removable = canManage && !isOwner && !isMe;

          return (
            <li className="chat-participant" key={participant.userId}>
              {participant.avatar ? (
                <img className="chat-avatar chat-avatar--img" src={participant.avatar} alt="" />
              ) : (
                <span className="chat-avatar">{initialsOf(participant.fullName || '') || '?'}</span>
              )}

              <span className="chat-participant-main">
                <span className="chat-list-name">
                  {participant.fullName || t('chat.system.unknown')}
                  {isMe && <span className="chat-participant-you">{t('chat.participants.you')}</span>}
                </span>
                {(participant.role === 'OWNER' || participant.role === 'ADMIN') && (
                  <span className="chat-participant-role">
                    {t(`chat.participants.roles.${participant.role}`)}
                  </span>
                )}
              </span>

              {removable && (
                <button
                  type="button"
                  className="chat-icon-btn"
                  title={t('chat.participants.remove')}
                  disabled={busyId != null}
                  onClick={() => handleRemove(participant)}
                >
                  {busyId === participant.userId
                    ? <div className="strict-loader" />
                    : <Icon name="trash-2" size={16} />}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {myRole !== 'OWNER' && (
        <div className="chat-participants-foot">
          {confirmLeave ? (
            <>
              <span className="chat-modal-hint">{t('chat.participants.leaveConfirm')}</span>
              <div className="chat-participants-confirm">
                <button type="button" className="chat-role-chip" onClick={() => setConfirmLeave(false)}>
                  {t('chat.participants.leaveCancel')}
                </button>
                <button
                  type="button"
                  className="chat-leave-btn"
                  onClick={handleLeave}
                  disabled={busyId === 'self'}
                >
                  {busyId === 'self' ? t('chat.participants.leaving') : t('chat.participants.leaveAction')}
                </button>
              </div>
            </>
          ) : (
            <button type="button" className="chat-leave-btn" onClick={() => setConfirmLeave(true)}>
              <Icon name="log-out" size={16} />
              {t('chat.participants.leave')}
            </button>
          )}
        </div>
      )}
    </aside>
  );
};

export default ParticipantsPanel;
